import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const ImageCarousel = ({ images, altText, className = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!images || images.length <= 1 || isHovered) return;

    const interval = setInterval(() => {
      if (scrollRef.current) {
        const itemWidth = scrollRef.current.clientWidth;
        const maxScroll = scrollRef.current.scrollWidth - itemWidth;
        const currentScroll = scrollRef.current.scrollLeft;
        
        let nextScroll = currentScroll + itemWidth;
        if (nextScroll > maxScroll - 5) {
          nextScroll = 0; // volver al inicio
        }
        
        scrollRef.current.scrollTo({
          left: nextScroll,
          behavior: 'smooth'
        });
      }
    }, 3500); // Cambiar cada 3.5 segundos

    return () => clearInterval(interval);
  }, [images, isHovered]);

  if (!images || images.length === 0) return null;
  
  // Si solo hay una imagen, no mostrar carrusel, solo la imagen normal
  if (images.length === 1) {
    return (
      <img
        src={images[0]}
        alt={altText}
        className={`w-full h-full object-cover ${className}`}
      />
    );
  }

  const handleScroll = (e) => {
    const scrollPosition = e.target.scrollLeft;
    const itemWidth = e.target.clientWidth;
    const newIndex = Math.round(scrollPosition / itemWidth);
    if (newIndex !== currentIndex) {
      setCurrentIndex(newIndex);
    }
  };

  const scrollTo = (index) => {
    if (scrollRef.current) {
      const itemWidth = scrollRef.current.clientWidth;
      scrollRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth'
      });
    }
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (currentIndex < images.length - 1) {
      scrollTo(currentIndex + 1);
    } else {
      scrollTo(0); // loop back
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      scrollTo(currentIndex - 1);
    } else {
      scrollTo(images.length - 1); // loop back
    }
  };

  return (
    <div 
      className={`relative group w-full h-full overflow-hidden ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => {
        setTimeout(() => setIsHovered(false), 3000); // Resume auto-play after 3s on touch
      }}
    >
      <div 
        ref={scrollRef}
        className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onScroll={handleScroll}
      >
        {images.map((src, idx) => (
          <div key={idx} className="w-full h-full flex-shrink-0 snap-start">
            <img
              src={src}
              alt={`${altText} - Imagen ${idx + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {/* Flechas de navegación (visibles en hover en desktop) */}
      <button 
        onClick={prevImage}
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-slate-800 p-1.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 hidden sm:block"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button 
        onClick={nextImage}
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-slate-800 p-1.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 hidden sm:block"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Indicadores de paginación */}
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10">
        {images.map((_, idx) => (
          <button
            key={idx}
            onClick={(e) => { e.stopPropagation(); scrollTo(idx); }}
            className={`w-2 h-2 rounded-full transition-all shadow-sm ${
              idx === currentIndex ? 'bg-[#00C2CB] w-4' : 'bg-white/70 hover:bg-white'
            }`}
          />
        ))}
      </div>
    </div>
  );
};
